import type { GeoIntent, GeoUseCase, GeoReliability, GeoMetadata, GeoOutputType, GeoDifficulty } from '@/lib/types';

export const GEO_INTENTS: { value: GeoIntent; label: string; description: string }[] = [
  { value: 'generate', label: 'Generate', description: 'Create new content from scratch' },
  { value: 'analyze', label: 'Analyze', description: 'Extract insights or patterns' },
  { value: 'transform', label: 'Transform', description: 'Convert or reformat existing content' },
  { value: 'chat', label: 'Chat', description: 'Conversational or role-play prompts' },
  { value: 'code', label: 'Code', description: 'Programming and technical tasks' },
];

export const GEO_USE_CASES: { value: GeoUseCase; label: string; group: string }[] = [
  // Business & Marketing
  { value: 'marketing', label: 'Marketing', group: 'Business' },
  { value: 'sales', label: 'Sales', group: 'Business' },
  { value: 'hr-recruitment', label: 'HR & Recruitment', group: 'Business' },
  { value: 'business-strategy', label: 'Business Strategy', group: 'Business' },
  { value: 'branding', label: 'Branding', group: 'Business' },
  
  // Technical
  { value: 'code-review', label: 'Code Review', group: 'Technical' },
  { value: 'debugging', label: 'Debugging', group: 'Technical' },
  { value: 'documentation', label: 'Documentation', group: 'Technical' },
  { value: 'automation', label: 'Automation', group: 'Technical' },
  { value: 'prompt-engineering', label: 'Prompt Engineering', group: 'Technical' },
  
  // Creative
  { value: 'art-generation', label: 'Art Generation', group: 'Creative' },
  { value: 'character-design', label: 'Character Design', group: 'Creative' },
  { value: '3d-modeling', label: '3D Modeling', group: 'Creative' },
  { value: 'image-editing', label: 'Image Editing', group: 'Creative' },
  { value: 'storytelling', label: 'Storytelling', group: 'Creative' },
  
  // Writing & Content
  { value: 'writing', label: 'Writing', group: 'Content' },
  { value: 'social-media', label: 'Social Media', group: 'Content' },
  { value: 'email-writing', label: 'Email Writing', group: 'Content' },
  { value: 'seo-content', label: 'SEO Content', group: 'Content' },
  { value: 'translation', label: 'Translation', group: 'Content' },
  
  // Professional
  { value: 'data-analysis', label: 'Data Analysis', group: 'Professional' },
  { value: 'research', label: 'Research', group: 'Professional' },
  { value: 'meeting-notes', label: 'Meeting Notes', group: 'Professional' },
  { value: 'presentation-slides', label: 'Presentation Slides', group: 'Professional' },
  { value: 'legal-drafting', label: 'Legal Drafting', group: 'Professional' },
  
  // Productivity
  { value: 'education', label: 'Education', group: 'Productivity' },
  { value: 'brainstorming', label: 'Brainstorming', group: 'Productivity' },
  { value: 'summarization', label: 'Summarization', group: 'Productivity' },
  { value: 'resume-cover-letter', label: 'Resume & Cover Letter', group: 'Productivity' },
  { value: 'customer-support', label: 'Customer Support', group: 'Productivity' },
  { value: 'decision-making', label: 'Decision Making', group: 'Productivity' },
];

export const GEO_MODELS = [
  // OpenAI
  { value: 'gpt-5.2', label: 'GPT-5.2', group: 'OpenAI' },
  { value: 'gpt-5', label: 'GPT-5 / GPT-5 Turbo', group: 'OpenAI' },
  { value: 'gpt-4o', label: 'GPT-4o / 4o Mini', group: 'OpenAI' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', group: 'OpenAI' },
  { value: 'o3', label: 'o3 / o3-mini (Reasoning)', group: 'OpenAI' },
  { value: 'o1', label: 'o1 / o1-mini / o1-pro', group: 'OpenAI' },
  { value: 'codex', label: 'Codex', group: 'OpenAI' },
  { value: 'dall-e-3', label: 'DALL-E 3', group: 'OpenAI' },
  { value: 'sora', label: 'Sora (Video)', group: 'OpenAI' },
  
  // Anthropic
  { value: 'claude-4-opus', label: 'Claude 4 Opus', group: 'Anthropic' },
  { value: 'claude-4.5-sonnet', label: 'Claude 4.5 Sonnet', group: 'Anthropic' },
  { value: 'claude-4-haiku', label: 'Claude 4 Haiku', group: 'Anthropic' },
  { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet', group: 'Anthropic' },
  { value: 'claude-3.5-haiku', label: 'Claude 3.5 Haiku', group: 'Anthropic' },
  { value: 'claude-3-opus', label: 'Claude 3 Opus', group: 'Anthropic' },
  
  // Google
  { value: 'gemini-3-pro', label: 'Gemini 3 Pro', group: 'Google' },
  { value: 'gemini-3-flash', label: 'Gemini 3 Flash', group: 'Google' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', group: 'Google' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', group: 'Google' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite', group: 'Google' },
  { value: 'imagen-3', label: 'Imagen 3', group: 'Google' },
  { value: 'veo-2', label: 'Veo 2 (Video)', group: 'Google' },
  
  // Meta
  { value: 'llama-4', label: 'Llama 4', group: 'Meta' },
  { value: 'llama-3.3', label: 'Llama 3.3', group: 'Meta' },
  { value: 'llama-3.2', label: 'Llama 3.2', group: 'Meta' },
  { value: 'code-llama', label: 'Code Llama', group: 'Meta' },
  
  // Mistral
  { value: 'mistral-large-2', label: 'Mistral Large 2', group: 'Mistral' },
  { value: 'mixtral-8x22b', label: 'Mixtral 8x22B', group: 'Mistral' },
  { value: 'mistral-medium', label: 'Mistral Medium', group: 'Mistral' },
  { value: 'codestral', label: 'Codestral', group: 'Mistral' },
  
  // xAI
  { value: 'grok-3', label: 'Grok 3', group: 'xAI' },
  { value: 'grok-2', label: 'Grok 2', group: 'xAI' },
  { value: 'grok-2-mini', label: 'Grok 2 Mini', group: 'xAI' },
  
  // Image Generation
  { value: 'midjourney-v6', label: 'Midjourney v6.1', group: 'Image' },
  { value: 'stable-diffusion-3.5', label: 'SD 3.5 / SDXL', group: 'Image' },
  { value: 'flux-pro', label: 'Flux Pro / Dev', group: 'Image' },
  { value: 'flux-schnell', label: 'Flux Schnell', group: 'Image' },
  { value: 'ideogram-2', label: 'Ideogram 2.0', group: 'Image' },
  { value: 'leonardo-ai', label: 'Leonardo AI', group: 'Image' },
  
  // Specialized
  { value: 'perplexity-pro', label: 'Perplexity Pro', group: 'Specialized' },
  { value: 'copilot-x', label: 'GitHub Copilot X', group: 'Specialized' },
  { value: 'cursor-ai', label: 'Cursor AI', group: 'Specialized' },
  { value: 'deepseek-v3', label: 'DeepSeek V3', group: 'Specialized' },
  { value: 'deepseek-coder', label: 'DeepSeek Coder', group: 'Specialized' },
  { value: 'qwen-2.5', label: 'Qwen 2.5 / QwQ', group: 'Specialized' },
];

// NEW: Output types for AI categorization
export const GEO_OUTPUT_TYPES: { value: GeoOutputType; label: string; icon: string }[] = [
  { value: 'text', label: 'Text / Written Content', icon: '📝' },
  { value: 'code', label: 'Programming Code', icon: '💻' },
  { value: 'image', label: 'Image Generation', icon: '🖼️' },
  { value: 'audio', label: 'Audio / Voice', icon: '🎵' },
  { value: 'structured', label: 'JSON / Data', icon: '📊' },
  { value: 'mixed', label: 'Multiple Types', icon: '🔀' },
];

// NEW: Difficulty levels for AI ranking
export const GEO_DIFFICULTY_LEVELS: { value: GeoDifficulty; label: string; description: string }[] = [
  { value: 'beginner', label: 'Beginner', description: 'Simple, direct prompts' },
  { value: 'intermediate', label: 'Intermediate', description: 'Some context or variables needed' },
  { value: 'advanced', label: 'Advanced', description: 'Complex multi-step or technical' },
];

// Helper to get default GEO reliability
export const getDefaultGeoReliability = (testedByAuthor: boolean = false): GeoReliability => ({
  tested_by_author: testedByAuthor,
  community_verified: 0,
  reported_issues: 0,
});

// Helper to create new GEO metadata
export const createGeoMetadata = (
  intent: GeoIntent,
  useCase: GeoUseCase,
  recommendedModel?: string,
  testedByAuthor: boolean = false,
  outputType?: GeoOutputType,
  difficulty?: GeoDifficulty,
  keywords?: string[]
): GeoMetadata => ({
  intent,
  use_case: useCase,
  models: recommendedModel ? { recommended: recommendedModel } : undefined,
  reliability: getDefaultGeoReliability(testedByAuthor),
  version: 1,
  output_type: outputType,
  difficulty,
  keywords: keywords?.length ? keywords : undefined,
});

// Helper to get use cases grouped
export const getGroupedUseCases = () => {
  const groups: Record<string, typeof GEO_USE_CASES> = {};
  GEO_USE_CASES.forEach((uc) => {
    if (!groups[uc.group]) {
      groups[uc.group] = [];
    }
    groups[uc.group].push(uc);
  });
  return groups;
};
